import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { IndianRupee, CheckCircle } from "lucide-react";

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return "-";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
};

export const TdsAmountModal = ({
  tdsModal,
  setTdsModal,
  newAmount,
  setNewAmount,
  newTdsAmount,
  setNewTdsAmount,
  handleTdsAmountUpdate,
  getAffectedCount,
  transactions,
}) => {
  const close = () => setTdsModal({ open: false, txn: null, applyTo: "single" });
  const txn = tdsModal.txn;
  const affectedCount = getAffectedCount();

  return (
    <Dialog open={tdsModal.open} onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IndianRupee className="w-5 h-5" />
            Edit TDS Amount
          </DialogTitle>
          <DialogDescription>Fix incorrect TDS amounts for this transaction</DialogDescription>
        </DialogHeader>

        {txn && (
          <div className="py-4 space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg space-y-1">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><strong>Date:</strong> {txn.date}</div>
                <div><strong>Voucher:</strong> {txn.voucher_no || "-"}</div>
                <div><strong>Party:</strong> {txn.party_name}</div>
                <div><strong>Section:</strong> {txn.section || "-"}</div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm pt-2 border-t mt-2">
                <div><strong>Current Amount:</strong> <span className="text-slate-600">{formatCurrency(txn.amount)}</span></div>
                <div><strong>Current TDS:</strong> <span className="text-emerald-600">{formatCurrency(txn.tds_amount)}</span></div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Apply fix to:</Label>
              <RadioGroup
                value={tdsModal.applyTo}
                onValueChange={(val) => setTdsModal({ ...tdsModal, applyTo: val })}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2 p-2 rounded-lg border hover:bg-slate-50">
                  <RadioGroupItem value="single" id="apply-single" />
                  <Label htmlFor="apply-single" className="flex-1 cursor-pointer">
                    <span className="font-medium">This transaction only</span>
                    <span className="text-slate-500 text-xs ml-2">(1 record)</span>
                  </Label>
                </div>
                {txn.voucher_no && (
                  <div className="flex items-center space-x-2 p-2 rounded-lg border hover:bg-slate-50">
                    <RadioGroupItem value="voucher" id="apply-voucher" />
                    <Label htmlFor="apply-voucher" className="flex-1 cursor-pointer">
                      <span className="font-medium">All with voucher #{txn.voucher_no}</span>
                      <span className="text-slate-500 text-xs ml-2">
                        ({transactions.filter((t) => t.voucher_no === txn.voucher_no).length} record(s))
                      </span>
                    </Label>
                  </div>
                )}
                <div className="flex items-center space-x-2 p-2 rounded-lg border hover:bg-slate-50">
                  <RadioGroupItem value="party" id="apply-party" />
                  <Label htmlFor="apply-party" className="flex-1 cursor-pointer">
                    <span className="font-medium">All for "{txn.party_name}"</span>
                    <span className="text-slate-500 text-xs ml-2">
                      ({transactions.filter((t) => t.party_name === txn.party_name).length} record(s))
                    </span>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">New Base Amount</Label>
                <Input
                  type="number"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="mt-1 font-mono"
                  data-testid="new-amount-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">New TDS Amount</Label>
                <Input
                  type="number"
                  value={newTdsAmount}
                  onChange={(e) => setNewTdsAmount(e.target.value)}
                  placeholder="Enter TDS"
                  className="mt-1 font-mono"
                  data-testid="new-tds-input"
                />
              </div>
            </div>

            {newAmount && newTdsAmount && parseFloat(newAmount) > 0 && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  <strong>New TDS Rate:</strong>{" "}
                  {((parseFloat(newTdsAmount) / parseFloat(newAmount)) * 100).toFixed(2)}%
                </p>
                <p className="text-xs text-green-600 mt-1">
                  This will update <strong>{affectedCount}</strong> transaction(s)
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close} data-testid="tds-cancel-btn">Cancel</Button>
          <Button
            onClick={handleTdsAmountUpdate}
            disabled={!newTdsAmount || parseFloat(newTdsAmount) < 0}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="tds-confirm-btn"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Update TDS ({affectedCount} record{affectedCount !== 1 ? "s" : ""})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
